import React from 'react';
import { LogOut, Leaf, Map, Gamepad2, Wallet } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useWax } from '../context/WaxContext';
import VerticalFarmTower from './VerticalFarmTower';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { waxActor, waxConnecting, connect, disconnect } = useWax();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      {/* Header */}
      <header className="w-full px-4 py-4 border-b border-white/5">
        <div className="w-full flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center">
              <span className="text-lg font-black">A</span>
            </div>
            <div>
              <h1 className="text-lg font-bold">Agri<span className="text-green-400">VoLED</span></h1>
              <p className="text-xs text-slate-500">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {waxActor ? (
              <button onClick={disconnect}
                className="px-3 py-1.5 text-xs bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/20 transition-all">
                WAX: {waxActor}
              </button>
            ) : (
              <button onClick={connect} disabled={waxConnecting}
                className="px-3 py-1.5 text-xs bg-white/5 border border-white/10 text-slate-400 rounded-lg hover:bg-white/10 transition-all">
                {waxConnecting ? 'Connecting...' : 'Connect WAX'}
              </button>
            )}
            <button onClick={logout}
              className="p-2 text-slate-400 hover:text-white transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="w-full px-4 py-8">
        {/* Wallet bar */}
        <div className="flex gap-4 mb-8">
          <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl">
            <Wallet className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-semibold">{user?.ahant_balance ?? 0} AHN</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl">
            <Leaf className="w-4 h-4 text-green-400" />
            <span className="text-sm font-semibold">{user?.urja_balance ?? 0} URJA</span>
          </div>
        </div>

        {/* Vertical Farm Animation */}
        <div className="flex justify-center mb-8">
          <div className="w-64">
            <VerticalFarmTower />
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="p-6 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/[0.07] transition-all cursor-pointer">
            <Map className="w-8 h-8 text-green-400 mb-3" />
            <h3 className="text-lg font-bold mb-1">Digital Twin</h3>
            <p className="text-sm text-slate-400">Live model of every site, crop and batch. Every claim has records behind it.</p>
          </div>
          <div className="p-6 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/[0.07] transition-all cursor-pointer">
            <Gamepad2 className="w-8 h-8 text-emerald-400 mb-3" />
            <h3 className="text-lg font-bold mb-1">The Game</h3>
            <p className="text-sm text-slate-400">Strategy game built on the same data model. Run a food company across every layer of the food system.</p>
          </div>
          <div className="p-6 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/[0.07] transition-all cursor-pointer">
            <Leaf className="w-8 h-8 text-lime-400 mb-3" />
            <h3 className="text-lg font-bold mb-1">Growing Sites</h3>
            <p className="text-sm text-slate-400">No-dig beds, polytunnels, mushroom rooms and rooftops. A network of small sites close to the people who eat the food.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
